import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import ConfirmationModal from '../components/ConfirmationModal';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { API } from '../config';
import axios from 'axios';
import './BusinessUsers.css';

const BusinessUsers = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, error } = useNotification();
  const [users, setUsers] = useState([]);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    name: '',
    phone: '',
    role: 'manager',
    isActive: true
  });

  useEffect(() => {
    fetchBusiness();
    fetchUsers();
  }, [businessId]);

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${API}/businesses/${businessId}`);
      setBusiness(response.data.business);
    } catch (error) {
      console.error('Error fetching business:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/businesses/${businessId}/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userItem) => {
    setEditingUser(userItem);
    setFormData({
      username: userItem.username,
      email: userItem.email,
      name: userItem.name || '',
      phone: userItem.phone || '',
      role: userItem.role,
      isActive: userItem.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, formData);
        success('User updated successfully!');
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        name: '',
        phone: '',
        role: 'manager',
        isActive: true
      });
      fetchUsers();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleDelete = (userId) => {
    setUserToDelete(userId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/users/${userToDelete}`);
      success('User deleted successfully!');
      setShowConfirmModal(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      error(getErrorMessage(err));
      setShowConfirmModal(false);
      setUserToDelete(null);
    }
  };

  return (
    <Layout>
      <div className="business-users-page">
        <div className="page-header">
          <div>
            <button onClick={() => navigate('/businesses')} className="back-button">
              ← Back
            </button>
            <h1>Users - {business?.name || 'Loading...'}</h1>
            <p className="page-subtitle">Manage users for this business</p>
          </div>
        </div>

        {loading ? (
          <Loader message="Loading users..." variant="block" />
        ) : (
          <div className="table-container">
            {users.length === 0 ? (
              <div className="empty-state">
                <p>No users found for this business.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Branch</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((userItem) => (
                    <tr key={userItem.id}>
                      <td className="font-semibold">{userItem.username}</td>
                      <td>{userItem.name || '—'}</td>
                      <td>{userItem.email || '—'}</td>
                      <td>{userItem.phone || '—'}</td>
                      <td>
                        <span className="badge">{userItem.role?.replace('_', ' ').toUpperCase()}</span>
                      </td>
                      <td>{userItem.branch?.name || '—'}</td>
                      <td>
                        <span className={`status-badge ${userItem.is_active ? 'active' : 'inactive'}`}>
                          {userItem.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="created-at-cell">{formatDate(userItem.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn edit-btn" 
                            title="Edit"
                            onClick={() => handleEdit(userItem)}
                          >
                            ✏️
                          </button>
                          <button 
                            className="action-btn delete-btn" 
                            title="Delete"
                            onClick={() => handleDelete(userItem.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingUser(null); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingUser ? 'Edit User' : 'Create User'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="e.g. hanz_latest23"
                    required
                    disabled={!!editingUser}
                  />
                </div>
                <div className="form-group">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g. user@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                {user.role === 'super_admin' && (
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => { setShowModal(false); setEditingUser(null); }}>Cancel</button>
                  <button type="submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => { setShowConfirmModal(false); setUserToDelete(null); }}
          onConfirm={confirmDelete}
          title="Delete User"
          message="Are you sure you want to delete this user? This will soft delete it."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default BusinessUsers;
